export var ColorType = /*#__PURE__*/function (ColorType) {
  ColorType[ColorType["HSL"] = 0] = "HSL";
  ColorType[ColorType["HEX"] = 1] = "HEX";
  return ColorType;
}(ColorType || {});
export const DEFAULT_SHIFTBASE_COLORS = ['#8cbb26', '#008e5b', '#0696bb', '#2a71b0', '#444e99', '#6d398b', '#c4037d', '#e32322', '#ea621f', '#f18e1c', '#fdc60b', '#f4e500'];
/**
 * Returns a random default color from the list of default Shiftbase colors.
 * @returns A string representing a random default color.
 */
export function getRandomDefaultColor() {
  return DEFAULT_SHIFTBASE_COLORS[Math.floor(Math.random() * DEFAULT_SHIFTBASE_COLORS.length)];
}
/**
 * Returns a default color from the Shiftbase color palette based on the provided seed.
 *
 * @param seed - The seed used to determine the color.
 * @returns The default color from the Shiftbase color palette.
 */
export function getSeededDefaultColor(seed) {
  const id = parseInt(seed, 10);
  const colorIndex = id % DEFAULT_SHIFTBASE_COLORS.length;
  return DEFAULT_SHIFTBASE_COLORS[colorIndex];
}
/**
 * Lightens a given hex color by adjusting its luminosity.
 *
 * @param hexcolor - The hex color to lighten.
 * @param output - Optional. The desired output color format. Defaults to HSL.
 * @param forceLuminosity - Optional. The desired luminosity value. If provided, it overrides the calculated luminosity.
 * @returns The lightened color in the specified format (default: HSL).
 *
 * @link https://gist.github.com/ciembor/1494530
 */
export function lightenHex(hexcolor, output, forceLuminosity) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexcolor);
  if (!result || (result === null || result === void 0 ? void 0 : result.length) < 3) {
    return 'transparent';
  }
  let r = parseInt(result[1], 16);
  let g = parseInt(result[2], 16);
  let b = parseInt(result[3], 16);
  r /= 255, g /= 255, b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;
  if (max == min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
    }
    h /= 6;
  }
  s = s * 100;
  s = Math.round(s);
  l = l * 100;
  l = Math.round(l);
  h = Math.round(360 * h);
  //Invert luminosity when it is more than 80, This will prevent visibility issues.
  const luminosity = forceLuminosity ? forceLuminosity : l > 80 ? l - 30 : 90;
  const colorInHSL = 'hsl(' + h + ', ' + s + '%, ' + luminosity + '%)';
  if (output === ColorType.HEX) {
    return hslToHex(h, s, luminosity);
  }
  return colorInHSL;
}
/**
 * Converts HSL (Hue, Saturation, Lightness) color values to a hexadecimal color string.
 * @param h - The hue value (0-360).
 * @param s - The saturation value (0-100).
 * @param l - The lightness value (0-100).
 * @returns The hexadecimal color string.
 */
export function hslToHex(h, s, l) {
  l /= 100;
  const a = s * Math.min(l, 1 - l) / 100;
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0'); // convert to Hex and prefix "0" if needed
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}
